import { getFirestore, doc, getDoc, updateDoc, setDoc, runTransaction } from "firebase/firestore"; // setDoc not needed if we assume user exists
import { app } from "./firebase-config.js";

const db = getFirestore(app);

export const HistoryManager = {
    /**
     * Saves the current reading progress for a user.
     * Updates 'readHistory' field in the user document.
     * Manages a maximum of 20 history entries.
     * 
     * @param {Object} user - The current user object (must have uid).
     * @param {Object} series - The series object (id, title, thumbnail, type).
     * @param {Object} episode - The episode object (id, order(or index)).
     */
    saveReadingProgress: async (user, series, episode) => {
        if (!user || !user.uid) return;

        const userRef = doc(db, "users", user.uid);

        try {
            // OPTIMIZATION: Use Get + Set(Merge) instead of Transaction to reduce contention.
            // Transactions fail (precondition error) if *any* field (e.g. points) changes during execution.
            // Since we only care about this specific series entry, we can be looser.

            const userSnap = await getDoc(userRef);
            if (!userSnap.exists()) return;

            const userData = userSnap.data();
            const history = userData.readHistory || {};
            const existingEntry = history[series.id];

            const now = Date.now();
            let newLastRewardAt = existingEntry?.lastRewardAt || 0;

            const lastRewardDate = new Date(newLastRewardAt).toLocaleDateString();
            const todayDate = new Date(now).toLocaleDateString();

            if (lastRewardDate !== todayDate) {
                newLastRewardAt = now;
            }

            const entry = {
                seriesTitle: series.title || 'Untitled Series',
                thumbnail: series.thumbnail || series.cover || '',
                lastEpisodeId: episode.id || 'unknown_episode',
                lastEpisodeOrder: episode.order || episode.index || 0,
                // [RESUME] Save Scroll Position
                scrollPosition: episode.scrollPosition || 0,
                progress: episode.progress || 0,
                contentType: series.type || 'webtoon',
                updatedAt: now,
                lastRewardAt: newLastRewardAt
            };

            // Atomic Update using Dot Notation or Sub-Merge
            // We use setDoc with merge to ensure nested field creation without overwriting others
            await setDoc(userRef, {
                readHistory: {
                    [series.id]: entry
                }
            }, { merge: true });

            // Note: Pruning (Max 100) is temporarily removed to favor stability.
            // If checking strict 100 limit is needed, intended solution is migrating to Subcollection (HistoryService).

            // Update window local cache aggressively for UI responsiveness
            if (window.user && window.user.uid === user.uid) {
                if (!window.user.readHistory) window.user.readHistory = {};
                window.user.readHistory[series.id] = entry;
            }

        } catch (e) {
            console.error("Failed to save reading progress:", e);
        }
    },

    /**
     * Returns the reading history sorted by most recent.
     * @param {Object} user 
     * @returns {Array} List of history objects with seriesId injected.
     */
    getSortedHistory: (user) => {
        if (!user || !user.readHistory) return [];

        const history = user.readHistory;
        return Object.keys(history)
            .map(key => ({ ...history[key], seriesId: key }))
            .sort((a, b) => b.updatedAt - a.updatedAt);
    },

    /**
     * Removes a specific series from reading history.
     */
    removeHistoryItem: async (userUid, seriesId) => {
        if (!userUid || !seriesId) return;
        try {
            const userRef = doc(db, 'users', userUid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
                const history = userSnap.data().readHistory || {};
                if (history[seriesId]) {
                    delete history[seriesId];
                    await updateDoc(userRef, { readHistory: history });

                    if (window.user && window.user.uid === userUid) {
                        window.user.readHistory = history;
                    }
                }
            }
        } catch (e) {
            console.error("Failed to remove history item", e);
        }
    }
};

// Expose to window for easy access
window.HistoryManager = HistoryManager;
